import { getNativeBridgeFunction } from 'libs/shared-ui/src/commonUtils/nativeBridge';
import { ConnectError } from '@bufbuild/connect';
import {
  Loader,
  callTeacherEventsApi,
  clearLocalStorageKeys,
  deleteOfflineAccessKeyFromCache,
  deserify,
  getDeviceType,
  getLocalStorage,
  setLocalStorage,
  storeOfflineAccessKeyInCache,
  useCommonServiceClientContext,
  useNotificationPermission,
} from '@geneo2-web/shared-ui';
import {
  DeviceEnum,
  ProfileRolesEnum,
} from '@protos/user_management/ums.db_pb';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppSelector,
  useDeserifiedSelector } from '../../../../reduxStore/reduxHooks';
import { HOME, SCREEN_RESOLVER } from '../../../../routeHandling/RoutesNomenclature';
import { resetAnalyticsState } from '../../../Analytics/reducer/analytics.slice';
import { aiBookCloseEvent } from '../../../Home/home.events';
import { resetHomeDashboardState } from '../../../Home/reducer/homeDashboard.slice';
import { aiHomeworkCloseEvent } from '../../../Homework/homework.events';

import { resetManageHWState } from '../../../ManageHomework/reducer/manageHomework.slice';
import {
  resetTeachState,
  setSelectedResourceSessionId,
} from '../../../Teach/reducer/teach.slice';
import {
  aiChapterCloseEvent,
  aiLessonCloseEvent,
  aiTopicCloseEvent,
} from '../../../Teach/teach.events';
import {
  aiLoginEvent,
  aiLogoutEvent,
  interactionEvent,
} from '../../auth.events';
import {
  resetAuthState,
  setIsLoggingOut,
  setIsMCBUser,
  setUserInfo,
} from '../../reducer/auth.slice';
import { MCBErrorScreen } from './MCBErrorScreen';
import { getSessionData } from '@events/teacher/eventsCreator/utils';
// import { useDownloadContext } from 'apps/teacher/src/app/Context/DownloadContextProviderV2';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';
import { resetAssessmentFlowState } from '../../../Assessment/reducer/assessmentFlow.slice';
import { jwtDecode } from 'jwt-decode';

interface IBackendLogin {
  code?: string;
}

export const BackendLogin = (props: IBackendLogin) => {
  const { code } = props;
  const { isOffline } = useDownloadContext();
  console.log('isOffline', isOffline);
  const navigate = useNavigate();
  let auth = getLocalStorage('auth');
  if (auth === 'true' || auth === 'false') {
    auth = JSON.parse(auth);
  }
  const commonServiceClientContext = useCommonServiceClientContext();
  const {
    UmsSsoAPIServiceV1ClientWithStatusCodeHandler,
    UMSLoginAPIServiceV1ClientWithStatusCodeHandler,
    UmsUtilityAPIServiceV1ClientWithStatusCodeHandler,
  } = commonServiceClientContext;

  useEffect(() => {
    geneoLoginUsingMCBCode();
  }, []);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const dispatch = useDispatch();
  const deviceType = getDeviceType();
  const userInfo = deserify(useAppSelector((state) => state.auth.user_info));
  const userId = getLocalStorage('userId');
  const fcmToken = getLocalStorage('fcmToken'); // Convert fcmToken to string
  const deviceId = getLocalStorage('deviceID');

  function pushUserDetailsToAndroid(uToken: string, userID: string): void {
    try {
      if (getNativeBridgeFunction('pushUserDetails')) {
        getNativeBridgeFunction('pushUserDetails')?.(uToken, userID);
      }
    } catch (error) {
      console.log('Error pushing user details to Android', error);
    }
  }

  const { chapter_resources, selected_topic_info, selected_lessons_info } =
    deserify(useAppSelector((state) => state.teach));
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const { selected_assessment_id } = deserify(
    useAppSelector((state) => state.manageHomework)
  );

  const { permissionGranted, requestPermission } = useNotificationPermission(
    'teacher',
    isOffline
  );
  const closeAiFunctions = async () => {
    await aiLessonCloseEvent({
      lessonId: selected_lessons_info?.lessonId,
      isOffline: isOffline,
    });
    await aiTopicCloseEvent({
      topicId: selected_topic_info?.topicId,
      isOffline: isOffline,
    });
    await aiChapterCloseEvent({
      chapterId: chapter_resources?.chapterId,
      isOffline: isOffline,
    });
    await aiBookCloseEvent({
      bookId: class_subject_info?.bookId,
      isOffline: isOffline,
    });
    await aiHomeworkCloseEvent({
      homeworkId: selected_assessment_id,
      isOffline: isOffline,
    });
    await aiLogoutEvent({ isOffline: isOffline });
    dispatch(setSelectedResourceSessionId(undefined));
    // a call to clear backlog of events stored in indexDb
    !isOffline && (await callTeacherEventsApi(commonServiceClientContext));
  };

  const notificationAPI = async () => {
    if (!userId) {
      console.log('User ID not found');
      return;
    }
    if (isOffline) return;

    try {
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        profileId: BigInt(userId),
        role: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
        deviceType: DeviceEnum.DEVICE_TYPE_WEB,
        deviceToken: fcmToken?.toString(),
        isActive: false,
        deviceId: deviceId ? BigInt(deviceId) : undefined,
      });
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        deviceToken: fcmToken?.toString(),
        topic: `${String(userId)}_${ProfileRolesEnum.PROFILE_ROLE_TEACHER}`,
        subscribeTopic: false,
      });
    } catch (err) {
      console.error('Error in Notification API:', err);
    }
  };

  const handleLogout = async () => {
    try {
      dispatch(setIsLoggingOut(true));
      closeAiFunctions();
      if (fcmToken) {
        await notificationAPI();
      }
      const response =
        await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.logout({
          profileId: getLocalStorage('userId'),
          role: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
        });
    } catch (err) {
      console.log(err);
      dispatch(setIsLoggingOut(false));
    } finally {
      dispatch(resetAuthState());
      dispatch(resetHomeDashboardState());
      dispatch(resetAssessmentFlowState());
      dispatch(resetManageHWState());
      dispatch(resetTeachState());
      dispatch(resetAnalyticsState());
      // localStorage.clear();
      clearLocalStorageKeys();
      deleteOfflineAccessKeyFromCache();
      // navigate(LOGIN);
    }
  };

  const geneoLoginUsingMCBCode = async () => {
    try {
      if (code) {
        const res =
          await UmsSsoAPIServiceV1ClientWithStatusCodeHandler.validateUserSsoLogin(
            {
              code: code,
              role: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
              requestOrigin: 'MCB',
            }
          );
        const userId = res.userInfo?.profileId;
        if (auth && userId) {
          if (userId !== userInfo?.teacherProfileId) {
            await handleLogout();
          } else {
            const qrId = getLocalStorage('qrId');
            if(qrId){
              navigate(`${SCREEN_RESOLVER}?QRId=${qrId}`);
            } else {
              navigate(HOME);
            }
            return;
          }
        }
        const resSession =
          await UmsSsoAPIServiceV1ClientWithStatusCodeHandler.createSsoLoginSession(
            {
              profileId: userId,
            }
          );
        if (resSession.activeSessionId) {
          setLocalStorage(
            'activeSession',
            resSession.activeSessionId.toString()
          );
        }
        const response =
          await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.fetchTeacherProfile(
            {
              teacherProfileId: userId,
            }
          );
        const user_info = response.data;
        if (!user_info) {
          console.log('User info not present');
          return;
        }
        dispatch(setUserInfo(user_info));
        dispatch(setIsMCBUser(true));
        setLocalStorage('userId', user_info.teacherProfileId.toString());
        setLocalStorage('role', 'student');
        setLocalStorage('auth', 'true');
        const accessKey = user_info.schoolDetails[0]?.schoolId.toString()
          ? 'school-' + user_info.schoolDetails[0].schoolId.toString()
          : 'geneo';
        await storeOfflineAccessKeyInCache(accessKey);
        setTimeout(async () => {
          await interactionEvent({
            url: 'teacher_login',
            context: 'mcb_sso',
            name: 'Login',
            isOffline: isOffline,
          });
        }, 3000);
        await aiLoginEvent({
          teacherId: Number(user_info.teacherProfileId),
          sessionId: resSession.activeSessionId.toString(),
          isOffline: isOffline,
        });
        pushUserDetailsToAndroid(
          user_info.token,
          user_info.teacherProfileId.toString()
        );
        const qrId = getLocalStorage('qrId');
        if(qrId){
          navigate(`${SCREEN_RESOLVER}?QRId=${qrId}`);
        } else {
          navigate(HOME);
        }
        if (deviceType !== 'android') {
          await requestPermission();
        }
      }
    } catch (err: any) {
      if (err instanceof ConnectError) {
        setErrorMessage(err.message.replace(/.*\]\s*/, '').trim());
        return;
      }
      setErrorMessage('Internal Error');
    }
  };
  if (errorMessage) {
    return <MCBErrorScreen errorMessage={errorMessage} />;
  }
  return <Loader />;
};
